<script lang="ts">
  import { Camas, type Cama } from '../lib/api';

  let items = $state<Cama[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);

  let nueva = $state<Cama>({ numero: '', servicio: '', sala: '', estado: 'DISPONIBLE', esUci: false, activo: true });
  let editandoId = $state<string | null>(null);

  async function cargar() {
    loading = true; error = null;
    try { items = (await Camas.list()).filter(c => c.activo); }
    catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  function editar(c: Cama) {
    editandoId = c.id ?? null;
    nueva = { numero: c.numero, servicio: c.servicio ?? '', sala: c.sala ?? '', estado: c.estado, esUci: c.esUci ?? false, activo: c.activo ?? true };
  }

  function cancelarEdicion() {
    editandoId = null;
    nueva = { numero: '', servicio: '', sala: '', estado: 'DISPONIBLE', esUci: false, activo: true };
  }

  async function eliminar(c: Cama) {
    if (!confirm(`¿Eliminar la cama ${c.numero}?`)) return;
    try { await Camas.eliminar(c.id!); await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function guardar(e: SubmitEvent) {
    e.preventDefault();
    try {
      if (editandoId) {
        await Camas.actualizar(editandoId, { ...nueva });
      } else {
        await Camas.create({ ...nueva });
      }
      nueva = { numero: '', servicio: '', sala: '', estado: 'DISPONIBLE', esUci: false, activo: true };
      editandoId = null;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function cambiar(c: Cama, estado: string) {
    try { await Camas.estado(c.id!, estado); await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  $effect(() => { cargar(); });
</script>

<section>
  <header class="view-header">
    <div>
      <h2>Camas</h2>
      <p class="muted">Gestión de camas, estados y ocupación</p>
    </div>
    <div class="actions">
      <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
    </div>
  </header>

  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <form onsubmit={guardar} class="panel">
    <div class="row">
      <label class="field"><span>Número</span><input placeholder="C-501" bind:value={nueva.numero} required /></label>
      <label class="field"><span>Servicio</span><input placeholder="Servicio" bind:value={nueva.servicio} /></label>
      <label class="field"><span>Sala</span><input placeholder="Sala / piso" bind:value={nueva.sala} /></label>
      <label class="field"><span>Estado</span>
        <select bind:value={nueva.estado}>
          <option>DISPONIBLE</option><option>OCUPADA</option><option>SUCIA</option><option>LIMPIA</option>
          <option>MANTENIMIENTO</option><option>AISLAMIENTO</option><option>BLOQUEADA</option><option>ASIGNADA</option>
        </select>
      </label>
      <label class="chk field"><input type="checkbox" bind:checked={nueva.esUci} /> UCI</label>
      <button type="submit">{editandoId ? 'Guardar cambios' : 'Agregar'}</button>
      {#if editandoId}<button type="button" class="ghost" onclick={cancelarEdicion}>Cancelar</button>{/if}
    </div>
  </form>

  <div class="panel">
    <h3>Censo de camas</h3>
    <table>
      <thead><tr><th>Número</th><th>Servicio</th><th>Estado</th><th>UCI</th><th>Acciones</th></tr></thead>
      <tbody>
        {#each items as c (c.id)}
          <tr>
            <td>{c.numero}</td>
            <td>{c.servicio ?? '—'}</td>
            <td><span class="badge {c.estado.toLowerCase()}">{c.estado}</span></td>
            <td>{c.esUci ? 'Sí' : ''}</td>
            <td>
              <button onclick={() => editar(c)}>Editar</button>
              <button onclick={() => eliminar(c)}>Eliminar</button>
              {#if c.estado !== 'DISPONIBLE'}<button onclick={() => cambiar(c, 'DISPONIBLE')}>→ Disponible</button>{/if}
              {#if c.estado !== 'SUCIA'}<button onclick={() => cambiar(c, 'SUCIA')}>→ Sucia</button>{/if}
              {#if c.estado !== 'LIMPIA'}<button onclick={() => cambiar(c, 'LIMPIA')}>→ Limpia</button>{/if}
              {#if c.estado !== 'MANTENIMIENTO'}<button onclick={() => cambiar(c, 'MANTENIMIENTO')}>→ Mant.</button>{/if}
            </td>
          </tr>
        {:else}
          <tr><td colspan="5" class="empty">Sin camas.</td></tr>
        {/each}
      </tbody>
    </table>
  </div>
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  .actions { display: flex; gap: .5rem; flex-wrap: wrap; align-items: center; }
  .ghost { background: #fff; color: #16a34a; border: 1px solid #16a34a; border-radius: 8px; padding: .45rem .75rem; cursor: pointer; font: inherit; font-weight: 600; }
  .ghost:disabled { opacity: .55; cursor: not-allowed; }
  button { padding: .5rem .9rem; border: 0; border-radius: 8px; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; font: inherit; font-weight: 600; cursor: pointer; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  .ok { color: #16a34a; margin: .5rem 0; font-weight: 600; }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; margin-bottom: 1rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .panel h3 { margin: 0 0 .5rem; font-size: 1rem; color: #166534; }
  .row { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .row input, .row select { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; }
  .row input:focus, .row select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .chk { display: flex; align-items: center; gap: .25rem; font-size: .9rem; color: #374151; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .55rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .92rem; color: #111827; }
  .badge { padding: .15rem .55rem; border-radius: 999px; font-size: .75rem; font-weight: 600; background: #f3f4f6; color: #6b7280; }
  .badge.disponible { background: #dcfce7; color: #166534; }
  .badge.ocupada { background: #fee2e2; color: #dc2626; }
  .badge.sucia { background: #fef3c7; color: #d97706; }
  .badge.limpia { background: #dcfce7; color: #15803d; }
  .badge.mantenimiento { background: #f3f4f6; color: #6b7280; }
  .badge.aislamiento { background: #ede9fe; color: #6d28d9; }
  .badge.bloqueada { background: #f3f4f6; color: #374151; }
  .badge.asignada { background: #dcfce7; color: #166534; }
  .err { color: #dc2626; }
  .empty { color: #9ca3af; text-align: center; font-style: italic; }
  td button { margin-right: .25rem; padding: .25rem .5rem; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; color: #16a34a; cursor: pointer; font-size: .8rem; box-shadow: none; font-weight: 500; }
  td button:hover { transform: none; background: #f0fdf4; }
</style>
